import { useState, useRef, useEffect } from "react";
import { RefreshCw, UtensilsCrossed } from "lucide-react";
import ReviewCard from "./ReviewCard";
import RestaurantPostCard from "./RestaurantPostCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { resolveEatzyMediaUrl } from "@/lib/utils";

// Helper to convert database media paths to API URLs
function getMediaUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/api/')) return path;
  if (path.startsWith('data:')) return path;
  
  // Remove 'public/' prefix if present since the API endpoint adds it
  const cleanPath = path.startsWith('public/') ? path.substring(7) : path;
  return `/api/media/public/${cleanPath}`;
}

// Helper to format dates as relative time
const formatTimeAgo = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Recently';
  
  const date = new Date(dateString);
  
  // Check for invalid date
  if (isNaN(date.getTime())) return 'Recently';
  
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays === 0) {
    if (diffHours === 0) return "Just now";
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};


interface ReviewFeedProps {
  filter?: 'all' | 'friends' | 'nearby';
  searchQuery?: string;
  filters?: { cuisine: string[], location: string[], price: string[] };
  selectedExperienceTypes?: ('dine-in' | 'takeout' | 'delivery')[];
  highlightTags?: string[];
  priorityQuery?: string;
}

export default function ReviewFeed({ filter = 'all', searchQuery = '', filters = { cuisine: [], location: [], price: [] }, selectedExperienceTypes, highlightTags = [], priorityQuery }: ReviewFeedProps) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Build query params for filtering
  const queryParams = new URLSearchParams();
  if (highlightTags.length > 0) {
    queryParams.set('filterTags', highlightTags.join(','));
  }
  const queryString = queryParams.toString();
  const feedUrl = queryString ? `/api/feed/reviews?${queryString}` : '/api/feed/reviews';
  
  // Fetch reviews from API (using feed endpoint to get items with images)
  const { data: rawApiReviews = [], isLoading, refetch } = useQuery<any[]>({
    queryKey: ['/api/feed/reviews', highlightTags.join(',')],
    queryFn: async () => {
      const _tok = localStorage.getItem('token');
      const response = await fetch(feedUrl, {
        headers: _tok && _tok !== 'null' && _tok !== 'undefined' ? { 'Authorization': `Bearer ${_tok}` } : {}
      });
      if (!response.ok) throw new Error('Failed to fetch reviews');
      return response.json();
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
  
  // Transform API response to match ReviewCard props
  // API returns user.displayName, ReviewCard expects user.name
  const apiReviews = rawApiReviews.map((review: any) => ({
    ...review,
    user: {
      ...review.user,
      id: review.userId || review.user?.id, // Pass userId for follow functionality
      name: review.user?.name || review.user?.displayName || review.user?.username || 'Unknown',
      verified: review.user?.trustTier === 'elite' || review.user?.trustTier === 'trusted',
      followersCount: review.user?.followersCount || 0,
    },
    restaurant: {
      ...review.restaurant,
      location: review.restaurant?.location || review.restaurant?.city || review.restaurant?.address || 'Unknown location',
      city: review.restaurant?.city || 'Unknown',
      overallRating: review.restaurant?.overallRating || 8.5,
      totalReviews: review.restaurant?.totalReviews || 100,
      restaurantImage: review.restaurant?.primaryImageUrl ||
        review.restaurant?.logoImageUrl ||
        (review.restaurant?.googlePhotoName
          ? `/api/media/places-photo?name=${encodeURIComponent(review.restaurant.googlePhotoName)}`
          : undefined),
    },
    image: getMediaUrl(review.image || review.images?.[0] || '') || undefined,
    images: review.images?.map((img: string) => getMediaUrl(img)) || [],
    video: review.video ? getMediaUrl(review.video) : undefined,
    items: review.items?.map((item: any) => ({
      ...item,
      image: getMediaUrl(item.image || '') || item.image,
    })),
    comments: review.commentsCount || 0,
    createdAt: review.createdAt || 'Recently',
    tags: review.tags || [],
  }));
  
  const allReviews = apiReviews;
  
  // Filter reviews based on search and filters
  const getFilteredReviews = () => {
    let filtered = [...allReviews];
    
    // Apply text search
    if (searchQuery.trim()) {
      filtered = filtered.filter(review => 
        review.restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.dish?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        review.tags?.some((tag: string) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    // Apply cuisine filters
    if (filters?.cuisine?.length > 0) {
      filtered = filtered.filter(review => 
        filters?.cuisine?.some(cuisine => 
          review.tags?.some((tag: string) => tag.toLowerCase().includes(cuisine.toLowerCase())) ||
          review.restaurant.name.toLowerCase().includes(cuisine.toLowerCase()) ||
          review.dish?.name.toLowerCase().includes(cuisine.toLowerCase())
        )
      );
    }
    
    // Apply location filters
    if (filters?.location?.length > 0) {
      filtered = filtered.filter(review => 
        filters?.location?.some(location => 
          review.restaurant.location.toLowerCase().includes(location.toLowerCase())
        )
      );
    }
    
    // Apply price filters
    if (filters?.price?.length > 0) {
      filtered = filtered.filter(review => {
        if (!review.dish?.price) return false; // Only show reviews with dish prices
        
        const price = review.dish.price;
        return filters?.price?.some(priceRange => {
          switch (priceRange) {
            case '$': return price < 15;
            case '$$': return price >= 15 && price < 25;
            case '$$$': return price >= 25 && price < 40;
            case '$$$$': return price >= 40;
            default: return false;
          }
        });
      });
    }
    
    // Apply experience type filter (mock implementation) - multi-select support
    if (selectedExperienceTypes && selectedExperienceTypes.length > 0 && selectedExperienceTypes.length < 3) {
      // Only filter if not all types are selected (optimization: all selected = no filter needed)
      // For demo purposes, filter based on review tags and price ranges
      filtered = filtered.filter(review => {
        return selectedExperienceTypes.some(type => {
          switch (type) {
            case 'dine-in':
              return review.tags.includes('date-night') || review.tags.includes('atmosphere') || review.tags.includes('artisan') || (review.dish?.price ?? 0) > 20;
            case 'takeout':
              return review.tags.includes('comfort-food') || review.tags.includes('quick') || review.tags.includes('artisan') || (review.dish?.price ?? 0) <= 25;
            case 'delivery':
              return review.tags.includes('comfort-food') || review.tags.includes('warming') || review.tags.includes('wood-fired') || review.restaurant.location.includes('Downtown') || review.dish?.name.toLowerCase().includes('pizza');
            default:
              return true;
          }
        });
      });
    }

    // Apply feed type filter
    if (filter === 'friends') {
      // Show only verified/trusted users
      filtered = filtered.filter(review => 
        review.user.verified || (review.user.trustScore && review.user.trustScore >= 85)
      );
    }
    // 'nearby' filter requires server-side geolocation — pass through all reviews for now

    // Priority sort: reviews matching the widget query float to the top (no reviews hidden)
    if (priorityQuery && priorityQuery.trim()) {
      const q = priorityQuery.toLowerCase().trim();
      const matches = (r: any) =>
        r.restaurant?.name?.toLowerCase().includes(q) ||
        r.dish?.name?.toLowerCase().includes(q) ||
        r.items?.some((it: any) => it.name?.toLowerCase().includes(q)) ||
        r.text?.toLowerCase().includes(q) ||
        r.tags?.some((t: string) => t.toLowerCase().includes(q));
      filtered = [
        ...filtered.filter(matches),
        ...filtered.filter(r => !matches(r)),
      ];
    }
    
    return filtered;
  };
  
  const reviews = getFilteredReviews();
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleRefresh = async () => {
    console.log('Refreshing feed...');
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  // Infinite scroll removed for now - can be added when API supports pagination

  return (
    <div 
      ref={scrollRef}
      className="flex-1 overflow-y-auto scrollbar-hide"
      data-testid="review-feed"
    >
      {/* Pull to refresh indicator */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-center p-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span className="text-xs">
              {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
            </span>
          </Button>
        </div>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="px-4 py-2 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-md border border-border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-48 w-full rounded-md" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
              <div className="flex gap-4 pt-1">
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-12" />
                <Skeleton className="h-3 w-12" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && reviews.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="rounded-full bg-muted p-5 mb-4">
            <UtensilsCrossed className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-base font-semibold mb-1">No reviews yet</h3>
          <p className="text-sm text-muted-foreground max-w-xs">
            Be the first to share your dining experience. Post a review and earn Kudoz!
          </p>
        </div>
      )}

      {/* Reviews */}
      {!isLoading && reviews.length > 0 && (
        <div className="px-4 py-2 space-y-4">
          {reviews.map((review) => (
            review.postType === 'restaurant_post' ? (
              <RestaurantPostCard 
                key={review.id} 
                id={review.id}
                restaurant={{ ...review.restaurant, logo: review.restaurant.logoImageUrl, orderingEnabled: true }}
                text={review.text}
                images={review.images}
                image={review.mediaType === 'video' ? undefined : resolveEatzyMediaUrl(review.mediaUrl || review.items?.[0]?.image)}
                video={review.mediaType === 'video' ? resolveEatzyMediaUrl(review.mediaUrl) : undefined}
                mediaUrl={resolveEatzyMediaUrl(review.mediaUrl)}
                mediaType={review.mediaType}
                items={review.items}
                createdAt={review.createdAt}
                likes={review.likes}
                comments={review.comments}
                helpfulUpVotes={review.helpfulUpVotes}
                helpfulDownVotes={review.helpfulDownVotes}
                tags={review.tags}
                promotionDetails={review.promotionDetails}
                featured={review.featured}
                isLiked={review.isLiked}
                isHelpful={review.isHelpful}
                isBookmarked={review.isBookmarked}
              />
            ) : (
              <ReviewCard key={review.id} {...review} allReviews={allReviews} />
            )
          ))}
        </div>
      )}
    </div>
  );
}